import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { createPortal } from "react-dom";
import { useOrganisations } from "./hooks/useOrganisations";
import ReusableTable from "./components/ReusableTable/ReusableTable.jsx";
import ColumnSelector from "./components/ColumnSelector/ColumnSelector.jsx";
import ReusableSearchBar from "./components/ReusableSearchBar/ReusableSearchBar.jsx";
import { useSuspendReactivateAdmin } from "./hooks/useSuspendReactivateAdmin";
import { formatLocalDate } from "./utils/dateHelpers";
import CustomDropdown from "./components/extras/CustomDropdown.jsx";
import Loader from '../Loader.jsx';

import {
  validateOrganizationName,
  validateAdminName,
  validateEmail,
  validateContact,
  validateHrAllocated,
} from "./utils/validationHelpers";

import Swal from "sweetalert2";
import { Edit, Block, CheckCircle, Search, Refresh } from "@mui/icons-material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  ArrowUpAZ,
  ArrowDownZA,
  ArrowUpDown,
  Layout,
  MoreVertical,
  XCircleIcon
} from "lucide-react";
import {
  Tooltip,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../css/HRPortal.css";
import Pagination from "./Pagination";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

const OrganisationContent = () => {
  const [showOrganisations, setShowOrganisations] = useState(true);
  const navigate = useNavigate();
  const accessToken = sessionStorage.getItem("accessToken");
  const [currentAdminPage, setCurrentAdminPage] = useState(1);
  const itemsPerPage = 10;
  const [adminsPerPage] = useState(10);
  // const [selectedOrgAdminId, setSelectedOrgAdminId] = useState(null);
  // const [showSuspendBtn, setShowSuspendBtn] = useState(false);
  // const [ticketRemark, setTicketRemark] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [subscriptionFilter, setSubscriptionFilter] = useState("trial");
  const [editForm, setEditForm] = useState(false);
  const [editOrgId, setEditOrgId] = useState("");
  const [editOrgData, setEditOrgData] = useState(null);
  const [originalEndDate, setOriginalEndDate] = useState("");
  const [activeActionDropdown, setActiveActionDropdown] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    org_admin_name: true,
    organization_name: true,
    org_admin_contact: true,
    org_admin_email: true,
    created_at: true,
    end_date: true,
    action: true,
    account_manager_name: true,
  });
  // const [showColumnSelector, setShowColumnSelector] = useState(false);
  // const columnSelectorRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [formData, setFormData] = useState({
    organization_name: "",
    org_admin_name: "",
    org_admin_email: "",
    org_admin_contact: "",
    total_hr_allocated: "5",
    end_date: "",
    account_type: "Trial",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(null);
  const [validationErrors, setValidationErrors] = useState({
    organization_name: "",
    org_admin_name: "",
    org_admin_email: "",
    org_admin_contact: "",
    total_hr_allocated: "",
    end_date: "",
    account_type: "",
  });

  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });

  const columnOptions = [
    { key: "org_admin_name", label: "Admin Name" },
    { key: "organization_name", label: "Account Name" },
    { key: "org_admin_contact", label: "Admin Contact" },
    { key: "org_admin_email", label: "Email" },
    { key: "created_at", label: "Created Date" },
    { key: "end_date", label: "End Date" },
    { key: "account_manager_name", label: "Account Manager" },
    { key: "action", label: "Action" },
  ];

  // ==================== REUSABLE TABLE COLUMNS ====================
  const columns = [
    {
      key: "org_admin_name",
      label: "Admin Name",
      sortable: true,
    },
    {
      key: "organization_name",
      label: "Account Name",
      sortable: true,
    },
    {
      key: "org_admin_contact",
      label: "Admin Contact",
      sortable: false,
      render: (value, row) => (
        <Tooltip title={value || "NA"} placement="top">
          <span
            className="cursor-pointer hover:text-blue-300 transition-colors select-all"
            onClick={(e) => {
              e.stopPropagation();
              if (value) {
                navigator.clipboard.writeText(value);
                toast.success("Contact number copied!");
              }
            }}
          >
            {value || "NA"}
          </span>
        </Tooltip>
      ),
    },
    {
      key: "org_admin_email",
      label: "Email",
      sortable: true,
      render: (value, row) => (
        <Tooltip title={value || "NA"} placement="top">
          <span
            className="cursor-pointer hover:text-blue-300 transition-colors select-all"
            onClick={(e) => {
              e.stopPropagation();
              if (value) {
                navigator.clipboard.writeText(value);
                toast.success("Email copied!");
              }
            }}
          >
            {value && value.length > 20
              ? `${value.substring(0, 20)}...`
              : value || "NA"}
          </span>
        </Tooltip>
      ),
    },
    {
      key: "created_at",
      label: "Created Date",
      sortable: true,
      render: (value) => (value ? formatDate(value) : "NA"),
    },
    {
      key: "end_date",
      label: "End Date",
      sortable: true,
      render: (value) => (value ? formatDate(value) : "NA"),
    },
    {
      key: "account_manager_name",
      label: "Account Manager",
      sortable: false,
    },
    {
  key: "action",
  label: "Action",
  sortable: false,
  render: (_, row) => (
    <div className="relative">
      {/* 3 Dots Button */}
      <button
        className="p-2 rounded-full hover:bg-gray-700/50 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          setActiveActionDropdown(
            activeActionDropdown === row.organization_id
              ? null
              : row.organization_id
          );
          const rect = e.currentTarget.getBoundingClientRect();
          setDropdownPosition({
            // top: rect.bottom + window.scrollY + 4,
            top: rect.bottom + 4,
            // left: rect.right + window.scrollX - 160,
            left: rect.right - 160,
          });
        }}
      >
        <MoreVertical className="w-5 h-5 text-white" />
      </button>

      {/* Dropdown */}
      {activeActionDropdown === row.organization_id &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={(e) => {
                e.stopPropagation();
                setActiveActionDropdown(null);
              }}
            />

            <div
              className="fixed z-[9999] w-40 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleEditForm(row.organization_id);
                  setActiveActionDropdown(null);
                }}
                className="block w-full text-left px-4 py-2.5 text-sm hover:bg-gray-700 transition text-gray-300"
              >
                Edit
              </button>

              {(!row.is_suspended || row.is_suspended?.toLowerCase() !== "true") ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openSuspendModal(row.org_admin_id);  // Use hook to open modal
                    setActiveActionDropdown(null);
                  }}
                  className="block w-full text-left px-4 py-2.5 text-sm hover:bg-gray-700 transition text-gray-300"
                  disabled={actionLoading[row.org_admin_id]}
                >
                  {actionLoading[row.org_admin_id] ? "Suspending..." : "Suspend"}
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    reactivateAdmin(row.org_admin_id);
                    setActiveActionDropdown(null);
                  }}
                  className="block w-full text-left px-4 py-2.5 text-sm hover:bg-gray-700 transition text-green-400"
                  disabled={actionLoading[row.org_admin_id]}
                >
                  {actionLoading[row.org_admin_id] ? "Reactivating..." : "Reactivate"}
                </button>
              )}
            </div>
          </>,
          document.body
        )}
    </div>
  ),
}

  ];

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const confirmModalRef = useRef(null);
  const {
    data,
    loading,
    refresh,
    updateOrganisation,
    deleteOrganisation,
    // suspendAdmin,
    // reactivateAdmin,
    actionLoading,
  } = useOrganisations();

  // Reusable suspend/reactivate logic (replaces duplicate code)

  const {
      showSuspendModal,
      openSuspendModal,
      closeSuspendModal,
      reason,
      setReason,
      isSuspending,
      suspendAdmin,
      reactivateAdmin,
      isReactivating,
    } = useSuspendReactivateAdmin(() => refresh());

  // Close dropdown when scrolling
useEffect(() => {
  const handleScroll = () => {
    if (activeActionDropdown !== null) {
      setActiveActionDropdown(null);
    }
  };

  // Use capture phase to catch scroll events from any scrollable container
  window.addEventListener('scroll', handleScroll, true);
  
  return () => {
    window.removeEventListener('scroll', handleScroll, true);
  };
}, [activeActionDropdown]);

  useBodyScrollLock(editForm || showSuspendModal || isConfirmModalOpen);

  // useEffect(() => {
  //   setCurrentPage(1);
  // }, [subscriptionFilter]);

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  const handleDateSelect = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setDate(adjustedDate.getDate() + 1);
    const formattedDate = adjustedDate.toISOString().split("T")[0];
    setFormData({
      ...formData,
      end_date: formattedDate,
    });
    setCalendarDate(date);
    setShowCalendar(false);
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    if (name === "org_admin_contact") {
      if (!/^[0-9+() ]*$/.test(value)) {
        return;
      }
    }
    if (name === "total_hr_allocated") {
      if (!/^\d*$/.test(value)) {
        return;
      }
      if (value > 100) {
        return;
      }
    }

    let errorMessage = "";
    switch (name) {
      case "organization_name":
        errorMessage = validateOrganizationName(value);
        break;
      case "org_admin_email":
        errorMessage = validateEmail(value);
        break;
      case "org_admin_name":
        errorMessage = validateAdminName(value);
        break;
      case "org_admin_contact":
        errorMessage = validateContact(value);
        break;
      case "total_hr_allocated":
        errorMessage = validateHrAllocated(value);
        break;
      default:
        break;
    }
    setValidationErrors({
      ...validationErrors,
      [name]: errorMessage,
    });
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === "account_type" && value === "Paid") {
      if (!formData.end_date || formData.end_date === originalEndDate) {
        toast.warn("Please change the end date for Paid accounts.");
      }
    }
  };

  const validateForm = () => {
    const errors = {
      organization_name:
        validateOrganizationName(formData.organization_name) ||
        (formData.organization_name ? "" : "Account name is required"),
      org_admin_name:
        validateAdminName(formData.org_admin_name) ||
        (formData.org_admin_name ? "" : "Admin name is required"),
      org_admin_email:
        validateEmail(formData.org_admin_email) ||
        (formData.org_admin_email ? "" : "Admin email is required"),
    };

    if (
      formData.account_type === "Paid" &&
      (!formData.end_date || formData.end_date === originalEndDate)
    ) {
      errors.end_date = "Please change the end date for Paid accounts.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).every((key) => errors[key] === "");
  };

  const toggleEditForm = (orgId) => {
    if (editForm) {
      // Closing form
      setEditForm(false);
      setEditOrgId("");
      setFormData({
        organization_name: "",
        org_admin_name: "",
        org_admin_email: "",
        org_admin_contact: "",
        total_hr_allocated: "5",
        end_date: "",
        account_type: "Trial",
      });
      setOriginalEndDate("");
      setCalendarDate(null);
    } else {
      // Opening form – get data from already-loaded list
      const org = data.find(item => item.organization_id === orgId);
      if (!org) {
        toast.error("Organisation not found");
        return;
      }

      setEditOrgId(orgId);
      setEditOrgData(org);

      const endDate = org.end_date ? new Date(org.end_date) : new Date();
      const formatted = org.end_date
        ? new Date(org.end_date).toISOString().split("T")[0]
        : "";

      setFormData({
        organization_name: org.organization_name || "",
        org_admin_name: org.org_admin_name || "",
        org_admin_email: org.org_admin_email || "",
        org_admin_contact: org.org_admin_contact || "",
        total_hr_allocated: org.total_hr_allocated || "5",
        end_date: formatted,
        account_type: org.account_type || "Trial",
      });

      setOriginalEndDate(formatted);
      setCalendarDate(endDate);
      setEditForm(true);
    }
  };

  const handleFormUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await updateOrganisation(editOrgId, {
        ...formData,
        total_hr_allocated: parseInt(formData.total_hr_allocated),
      });
      toast.success("Account updated successfully");
      setEditForm(false);
    } catch (err) {
      // Error already toasted inside hook
    }
  };

  const handleDeleteOrg = (e) => {
    e.preventDefault();
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteOrganisation(editOrgId);
      toast.success("Account deleted successfully");
      setIsConfirmModalOpen(false);
      setEditForm(false);
    } catch (err) {
      // Error toasted in hook
    }
  };

  // 1. Filter by subscription type
  const subscriptionFilteredData =
    subscriptionFilter === "trial"
      ? data.filter((item) => item.account_type !== "Paid")
      : subscriptionFilter === "paid"
        ? data.filter((item) => item.account_type === "Paid")
        : data;

  // 2. Filter by search term
  const searchFilteredData = searchTerm
    ? subscriptionFilteredData.filter(
      (item) =>
        item.org_admin_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.org_admin_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.org_admin_contact?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : subscriptionFilteredData;

  // 3. SORT THE FULL DATASET (this is the key fix!)
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return searchFilteredData;

    return [...searchFilteredData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Date sorting (for created_at and end_date)
      if (sortConfig.key === "created_at" || sortConfig.key === "end_date") {
        const dateA = Date.parse(aValue);
        const dateB = Date.parse(bValue);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      // String comparison
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [searchFilteredData, sortConfig]);

  // 4. NOW paginate the already-sorted full list
  const indexOfLast = currentAdminPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentAdmins = sortedData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // const formatDate = (dateString) => {
  //   if (!dateString) return "NA";
  //   const date = new Date(dateString);
  //   if (isNaN(date.getTime())) return "NA"; // Handle invalid dates
  //   const year = date.getFullYear();
  //   const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based, add 1
  //   const day = String(date.getDate()).padStart(2, "0");
  //   return `${year}-${month}-${day}`;
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";
    const formatted = formatLocalDate(dateString);
    return formatted || "NA";  // Return "NA" if formatting fails
  };

  const showOrgData = (organizationId) => {
    console.log("Show account data for:", organizationId);
    setShowOrganisations(false);
    navigate(`/geniehireadmin/organisation/${organizationId}`);
  };

  return (
    <div className="glass-effect p-6 min-h-[120vh]">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl text-[#D9D9D9] font-normal">
            Accounts Management
          </h1>
        </div>
        <div className="text-sm text-gray-300">
          Total Accounts: {loading ? "..." : data.length}
        </div>
      </div>

      <div className="flex items-center justify-between mb-6 glass-effect p-4 rounded-lg">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-white">
            Subscription Type:
          </span>
          <div className="flex justify-between items-center border border-gray-700/50 rounded-full px-2 py-2 text-sm appearance-none backdrop-blur-lg">
            <button onClick={() => setSubscriptionFilter("trial")}>
              <span
                className={`text-white ${subscriptionFilter === "trial"
                    ? "bg-gradient-to-r from-pink-500 to-blue-500"
                    : null
                  } px-4 py-1 rounded-l-full`}
              >{`Trial`}</span>
            </button>
            <button onClick={() => setSubscriptionFilter("paid")}>
              <span
                className={`text-white ${subscriptionFilter === "paid"
                    ? "bg-gradient-to-r from-pink-500 to-blue-500"
                    : null
                  } px-4 py-1 rounded-r-full`}
              >{`Paid`}</span>
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <ColumnSelector
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            columnOptions={columnOptions}
            position="bottom-right"
          />
          <div className="flex items-center space-x-2">
            <ReusableSearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by account, admin, email or contact..."
              width="320px"
              minWidth="250px"
              maxWidth="360px"
            />
          </div>
        </div>
      </div>

      {loading ? (
        // <div className="text-center py-20 text-gray-400 text-lg font-medium">
        //   Loading organisations...
        // </div>
        <Loader />
      ) : data.length === 0 ? (
        <div className="text-center py-20 text-gray-400 text-lg">
          No organisations found
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          {/* Keep your existing <style jsx> block exactly as it is */}
          <style jsx>{`
            .scrollbar-thin::-webkit-scrollbar {
              width: 6px;
              height: 6px;
            }
            .scrollbar-thin::-webkit-scrollbar-track {
              background: rgba(255, 255, 255, 0.1);
              border-radius: 3px;
            }
            .scrollbar-thin::-webkit-scrollbar-thumb {
              background: rgba(255, 255, 255, 0.3);
              border-radius: 3px;
            }
            .scrollbar-thin::-webkit-scrollbar-thumb:hover {
              background: rgba(255, 255, 255, 0.5);
            }
            .scrollbar-thin::-webkit-scrollbar-corner {
              background: rgba(255, 255, 255, 0.1);
            }
            .email-column {
              max-width: 150px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          `}</style>

          <ReusableTable
            columns={columns}
            data={currentAdmins}
            visibleColumns={visibleColumns}
            noDataMessage="No accounts found"
            truncateLength={25}
            onRowClick={(row, event) => {
              if (event?.target?.closest('.action-dropdown-button') ||
                event?.target?.closest('.action-dropdown-menu')) {
                return;
              }
              showOrgData(row.organization_id);
            }}
            rowClassName="cursor-pointer hover:bg-gray-700/20 transition-colors"
            sortConfig={sortConfig}
            onSort={setSortConfig}
          />

          {/* Pagination - Keep this exactly as before */}
          {sortedData.length > itemsPerPage && (
            <Pagination
              currentPage={currentAdminPage}
              totalPages={totalPages}
              totalItems={sortedData.length}
              onPageChange={setCurrentAdminPage}
            />
          )}
        </div>
      )}

      {editForm && editOrgData && (
        <div className="fixed inset-0 flex items-center justify-center z-[99999999]">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            style={{
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          ></div>
          <div
            className="w-full max-w-lg p-6 rounded-lg shadow-lg relative mx-auto overflow-y-auto max-h-[67.5vh]"
            style={{
              background:
                "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
              backdropFilter: "blur(40px)",
              WebkitBackdropFilter: "blur(50px)",
              borderRadius: "10px",
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">
                Edit Account Details
              </h2>
              <button
                onClick={toggleEditForm}
                className="p-1.5 rounded-md hover:bg-slate-700 text-gray-400 hover:text-gray-300 transition-colors duration-150"
              >
                <CloseRounded className="text-red-400 cursor-pointer hover:text-red-300" />
              </button>
            </div>

            <form className="space-y-4">
              {[
                {
                  label: "Account Name",
                  name: "organization_name",
                  type: "text",
                  placeholder: "Enter Account name",
                },
                {
                  label: "Account Admin Name",
                  name: "org_admin_name",
                  type: "text",
                  placeholder: "Enter admin name",
                },
                {
                  label: "Account Admin Email",
                  name: "org_admin_email",
                  type: "text",
                  placeholder: "admin@company.com",
                },
                {
                  label: "Account Admin Contact",
                  name: "org_admin_contact",
                  type: "text",
                  placeholder: "Enter 10-digit number",
                },
                {
                  label: "Set Number Of HRs",
                  name: "total_hr_allocated",
                  type: "number",
                  min: "5",
                  placeholder: "Enter a positive number",
                },
              ].map(({ label, name, type = "text", placeholder, min }) => (
                <div key={name} className="mb-4">
                  <label className="block text-gray-300 mb-2">{label}</label>
                  <input
                    name={name}
                    type={type}
                    min={min}
                    placeholder={placeholder || ""}
                    value={formData[name] || ""}
                    onChange={handleFormChange}
                    className={`w-full px-3 py-2 bg-transparent border ${validationErrors[name]
                        ? "border-red-500"
                        : "border-gray-700/50"
                      } rounded text-[#CCCCCC] placeholder-gray-400 mt-2 focus:outline-none`}
                  />
                  {validationErrors[name] && (
                    <p className="text-red-400 text-sm mt-1">
                      {validationErrors[name]}
                    </p>
                  )}
                </div>
              ))}

              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Account Type</label>
                <CustomDropdown
                  options={[
                    { value: "Trial", label: "Trial" },
                    { value: "Paid", label: "Paid" },
                    { value: "Testing", label: "Testing" },
                  ]}
                  value={formData.account_type}
                  onChange={(value) =>
                    handleFormChange({
                      target: { name: "account_type", value },
                    })
                  }
                  className="mt-2"
                />
                {validationErrors.account_type && (
                  <p className="text-red-400 text-sm mt-1">
                    {validationErrors.account_type}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-300 mb-2">End Date</label>
                <div className="relative">
                  <input
                    name="end_date"
                    type="text"
                    placeholder="YYYY-MM-DD"
                    value={formData.end_date || ""}
                    onChange={handleFormChange}
                    className={`w-full px-3 py-2 bg-transparent border ${validationErrors.end_date
                        ? "border-red-500"
                        : "border-gray-700/50"
                      } rounded text-[#CCCCCC] placeholder-gray-400 mt-2 focus:outline-none`}
                    readOnly
                  />
                  <button
                    type="button"
                    onClick={toggleCalendar}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    📅
                  </button>
                  {showCalendar && calendarDate && (
                    <div className="absolute z-50 bottom-full mb-1 bg-[#0a1733] border border-[#5A4479] rounded-lg shadow-lg">
                      <Calendar
                        onChange={handleDateSelect}
                        value={
                          calendarDate ||
                          (formData.end_date
                            ? new Date(formData.end_date)
                            : new Date())
                        }
                        minDate={new Date()}
                        tileDisabled={({ date }) => isPastDate(date)}
                      />
                    </div>
                  )}
                </div>
                {validationErrors.end_date && (
                  <p className="text-red-400 text-sm mt-1">
                    {validationErrors.end_date}
                  </p>
                )}
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleFormUpdate}
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={handleDeleteOrg}
                  className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

            {/* ====================== REUSABLE SUSPEND MODAL (FIXED) ====================== */}
      {showSuspendModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              {/* Dark overlay */}
              <div
                className="absolute inset-0 bg-black bg-opacity-60"
                onClick={closeSuspendModal}
                style={{ backdropFilter: "blur(10px)" }}
              />
      
              {/* Modal card */}
              <div
                className="relative z-10 w-full max-w-xl p-6 rounded-lg shadow-2xl"
                style={{
                  background:
                    "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(50px)",
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-[#C1A0D0] text-lg">Suspend Organization Admin</h3>
                  <button onClick={closeSuspendModal}>
                    <CloseRoundedIcon className="h-6 w-6 text-[#C1A0D0] hover:text-white transition" />
                  </button>
                </div>
      
                <textarea
                  className="w-full px-4 py-3 bg-transparent border border-gray-600/50 rounded-lg text-[#CCCCCC] placeholder-gray-500 focus:outline-none focus:border-pink-500/50 resize-none"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for suspension (required)"
                  rows={5}
                />
      
                <div className="flex justify-end gap-4 mt-6 flex">
                  <button
                    onClick={closeSuspendModal}
                    className="px-8 py-2.5 border border-gray-600 text-[#CCCCCC] rounded-full hover:bg-gray-800/50 transition text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={suspendAdmin}
                    disabled={isSuspending || !reason.trim()}
                    className="px-8 py-2.5 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full hover:from-pink-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2 text-sm"
                  >
                    {isSuspending && (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    )}
                    {isSuspending ? "Suspending..." : "Suspend Admin"}
                  </button>
                </div>
              </div>
            </div>
          )}

      {isConfirmModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[99999999]">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            style={{
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          ></div>
          <div
            ref={confirmModalRef}
            className="w-full max-w-md p-6 rounded-lg shadow-lg relative mx-auto"
            style={{
              background:
                "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
              backdropFilter: "blur(40px)",
              WebkitBackdropFilter: "blur(50px)",
              borderRadius: "10px",
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">
                Confirm Deletion
              </h2>
              <button
                onClick={() => setIsConfirmModalOpen(false)}
                className="p-1.5 rounded-md hover:bg-slate-700 text-gray-400 hover:text-gray-300 transition-colors duration-150"
              >
                <CloseRounded className="text-red-400 cursor-pointer hover:text-red-300" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-gray-300">
                Are you sure you want to delete this account? This action cannot
                be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setIsConfirmModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganisationContent;